// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Skeleton } from '@mui/material';
import { Heading, Table, Tabs } from '@shared/ui-kit/components';
import type { ReactNode, FC } from 'react';
import { useAssetProfile } from '../hooks/use-asset-profile';
import {
  type AnalyzePopup,
  useAssetProfileTopHoldings,
} from '../hooks/use-asset-profile-top-holdings';
import {
  AssetProfileDescription,
  AssetProfileKeyStatistics,
} from '@shared/ui-kit/components/popups/asset-profile/components';
import { AssetProfileTopHoldings } from './asset-profile-top-holdings';
import { useTranslation } from 'react-i18next';

export type SupportedAssetTypes = 'etf' | 'mutual_fund';

export interface AssetProfileProps {
  readonly ticker: string;
  readonly assetType: SupportedAssetTypes;
  readonly watchlistButton?: ReactNode;
  readonly analyzePopup?: AnalyzePopup;
}

export const AssetProfile: FC<AssetProfileProps> = ({
  ticker,
  assetType,
  watchlistButton,
  analyzePopup,
}) => {
  const { t } = useTranslation();

  const { data, error, infoProperties, loading, statisticsProperties } =
    useAssetProfile({ ticker, assetType });

  const {
    pagination,
    holdings,
    loading: loadingHoldings,
    onAnalyzeHoldings,
    onDownloadHoldings,
  } = useAssetProfileTopHoldings({
    assetType,
    ticker,
    analyzePopup,
  });

  if (loading) {
    return <Skeleton variant="rounded" height={583} width={940} />;
  }

  if (error) {
    return (
      <p data-testid="error" className="text-title-dark ">
        {t('errors.asset-profile-error', { ns: 'errors' })}
      </p>
    );
  }

  return (
    <div className="w-[940px] text-title-dark">
      <div className="mb-2 flex items-center space-x-4">
        <Heading
          title={
            data ? (
              <>
                <span className="uppercase">{data.name}</span>
                {` | ${data.ticker}`}
              </>
            ) : (
              ''
            )
          }
          variant="h2"
        />
        {watchlistButton && watchlistButton}
      </div>

      <div className="mb-6 flex items-center justify-start">
        <span className="text-2xl font-bold tabular-nums" data-testid="info-price">
          <Table.Data
            value={data?.price}
            format="currency"
            currency={data?.currency}
            container={false}
          />
        </span>
      </div>

      <Tabs.Root defaultValue="profile">
        <Tabs.List>
          <Tabs.Trigger value="profile">
            {t('glossary.profile', { ns: 'glossary' })}
          </Tabs.Trigger>
          <Tabs.Trigger value="positions">
            {t('glossary.positions', { ns: 'glossary' })}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="profile">
          <div className="mt-4 grid gap-4">
            <AssetProfileDescription
              description={data?.description}
              infoProperties={infoProperties}
            />
            <AssetProfileKeyStatistics
              statisticsProperties={statisticsProperties}
            />
          </div>
        </Tabs.Content>

        <Tabs.Content value="positions" className="mt-4 flex justify-center">
          <AssetProfileTopHoldings
            pagination={pagination}
            holdings={holdings}
            loading={loadingHoldings}
            showAnalyzeButton={!!analyzePopup}
            onAnalyzeHoldings={onAnalyzeHoldings}
            onDownloadHoldings={onDownloadHoldings}
          />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};
